import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { AlertCircle, Calendar, Loader2, MessageSquare, Pencil, Phone, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatTemplateName } from '@/lib/templateName';
import { formatCredits } from '@/lib/creditCosts';
import { CUSTOM_MEDIA, COST_LABELS, type CampaignLaunchState } from '@/hooks/useCampaignLaunchState';
import { stepDef, type StepId } from '@/lib/campaignSteps';
import { StepFooter, StepHeader } from './StepLayout';

interface FinalPreviewStepProps {
  state: CampaignLaunchState;
  campaign: {
    name: string;
    description?: string | null;
    media?: { name: string } | null;
  };
  onBack: () => void;
  onEdit: (step: StepId) => void;
}

const AUDIENCE_LABELS: Record<string, string> = {
  selected: 'Selected contacts',
  filtered: 'Contacts matching a filter',
  all: 'All contacts',
};

/**
 * Step 5: everything chosen so far, read back in one place, then the launch.
 *
 * Nothing is edited here. Each section links to the step that owns it, and anything that would stop
 * the launch is listed with a link to where it is fixed, so a disabled Launch button is never a
 * dead end.
 */
export default function FinalPreviewStep({ state, campaign, onBack, onEdit }: FinalPreviewStepProps) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    channel, recipientCount, skippedNoPhone, selectedIds, mode, estimatedCredits, costLines, smsEncoding,
    selectedTemplate, whatsappTemplateId, requirements, headerText, dynamicButtons, buttonValues,
    needsMediaHeader, chosenAsset, mediaChoice, headerMediaUrl, uploadedMediaName, headerMediaId,
    customMessage, scheduled, scheduleDate, scheduleTime, launching, launchBlockers, launchBlocked,
    handleLaunch,
  } = state;

  const isWhatsApp = channel === 'whatsapp';

  // The balance beside the cost, from the live workspace query, so a launch that cannot be paid for
  // is visible before the click rather than as a refusal after it. The server is still the guard.
  const navigate = useNavigate();
  const { workspace, workspaceId, isSuperAdmin } = useWorkspace();
  const balance = workspace.creditsRemaining || 0;
  const shortBy = isSuperAdmin ? 0 : Math.max(0, estimatedCredits - balance);

  const headerMediaSummary = !needsMediaHeader
    ? null
    : headerMediaId
      ? `${uploadedMediaName || 'Uploaded file'} (uploaded)`
      : mediaChoice === CUSTOM_MEDIA
        ? headerMediaUrl || null
        : chosenAsset?.label || null;

  return (
    <Card className="p-6">
      <StepHeader step="launch" />

      <div className="space-y-4">
        <h3 className="text-sm font-semibold">{t('campaigns.preview_step.summary', 'Summary')}</h3>

        <div className="divide-y rounded-lg border">
          <SummarySection step="details" onEdit={onEdit}>
            <SummaryRow label="Name" value={campaign.name} />
            {campaign.description && <SummaryRow label="Description" value={campaign.description} multiline />}
          </SummarySection>

          <SummarySection step="channel" onEdit={onEdit}>
            <SummaryRow
              label="Channel"
              value={(
                <span className="inline-flex items-center gap-1">
                  {isWhatsApp ? <MessageSquare className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
                  {isWhatsApp ? 'WhatsApp' : 'SMS'}
                </span>
              )}
            />
            {isWhatsApp && (
              <SummaryRow
                label="Template"
                value={whatsappTemplateId && selectedTemplate
                  ? `${formatTemplateName(selectedTemplate.name)} (${selectedTemplate.language})`
                  : <Missing />}
              />
            )}
            {isWhatsApp && requirements?.header?.hasVariable && (
              <SummaryRow label="Header value" value={headerText.trim() || <Missing />} />
            )}
            {isWhatsApp && dynamicButtons.map((button) => (
              <SummaryRow
                key={button.index}
                label={`"${button.text || 'URL'}" link`}
                value={(buttonValues[String(button.index)] || '').trim() || <Missing />}
              />
            ))}
          </SummarySection>

          <SummarySection step="media" onEdit={onEdit}>
            <SummaryRow label="Media" value={campaign.media?.name || <Missing />} />
            {isWhatsApp && needsMediaHeader && (
              <SummaryRow label="Header media" value={headerMediaSummary || <Missing />} />
            )}
            {!isWhatsApp && (
              <SummaryRow
                label="Message"
                value={customMessage.trim() ? 'Your own message' : "The media's generated text"}
              />
            )}
          </SummarySection>

          <SummarySection step="audience" onEdit={onEdit}>
            <SummaryRow label="Audience" value={AUDIENCE_LABELS[mode] || mode} />
            <SummaryRow
              label="Recipients"
              value={recipientCount > 0 ? <strong>{recipientCount.toLocaleString()}</strong> : <Missing text="None" />}
            />
            {skippedNoPhone > 0 && (
              <p className="text-xs text-amber-600">
                {skippedNoPhone} of {selectedIds.length} selected contact
                {selectedIds.length === 1 ? '' : 's'} ha
                {skippedNoPhone === 1 ? 's' : 've'} no phone number and will be skipped.
              </p>
            )}
          </SummarySection>

          <div className="space-y-2 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Credits required</span>
              <span className="text-lg font-bold tabular-nums">{formatCredits(estimatedCredits)}</span>
            </div>
            {!isSuperAdmin && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Your balance</span>
                <span className={`tabular-nums ${shortBy > 0 ? 'font-medium text-destructive' : ''}`}>
                  {formatCredits(balance)}
                </span>
              </div>
            )}
            {/* Why that number: WhatsApp bills a higher rate the first time the workspace
                reaches a contact than for every message after. */}
            {costLines.length > 0 && (
              <div className="space-y-1 ps-2 border-s-2 border-muted">
                {costLines.map(([kind, line]) => (
                  <div key={kind} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {COST_LABELS[kind]} — {line.count.toLocaleString()} × {formatCredits(line.rate)}
                      {(line.segments ?? 1) > 1 ? ` × ${line.segments} segments` : ''}
                    </span>
                    <span>{formatCredits(line.credits)}</span>
                  </div>
                ))}
              </div>
            )}
            {/* SMS is billed per segment, and Arabic (or any emoji) forces Unicode, where a segment
                holds 70 characters rather than 160 — so a long message costs several times over. */}
            {!isWhatsApp && smsEncoding && smsEncoding.segments > 1 && (
              <p className="text-xs text-muted-foreground">
                This message is {smsEncoding.segments} SMS segments long
                {smsEncoding.encoding === 'UCS-2'
                  ? ' (Arabic or emoji: 70 characters per segment, 67 when split)'
                  : ' (160 characters per segment, 153 when split)'}
                , and each segment is charged.
              </p>
            )}
            {scheduled && scheduleDate && scheduleTime && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Scheduled for</span>
                <span className="font-medium">
                  {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Schedule option — DISABLED.
            Nothing in the backend processes a scheduled campaign: `launchCampaign` stores
            status='scheduled' and returns success, but no job, cron or runner ever picks it up,
            and credits are not deducted either. A scheduled campaign therefore sat forever
            while the UI reported success. Disabled rather than removed so re-enabling is a
            one-line change once a scheduler exists — see plans/templates-and-campaign-launch-fixes.md. */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-not-allowed opacity-60">
            <Checkbox checked={false} disabled />
            <Calendar className="h-4 w-4" />
            <span className="font-medium">Schedule for later</span>
            <Badge variant="outline" className="text-xs">Not available yet</Badge>
          </label>
          <p className="text-xs text-muted-foreground ps-6">
            Scheduling isn&apos;t running yet, so a scheduled campaign would never send. Use
            &quot;Launch campaign&quot; for the moment.
          </p>

          {scheduled && (
            <div className="grid grid-cols-2 gap-4 ps-6">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => state.setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => state.setScheduleTime(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {shortBy > 0 && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm" role="status">
            <p className="flex items-center gap-2 font-medium text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              You need {formatCredits(shortBy)} more credits to launch this campaign.
            </p>
            <button
              type="button"
              onClick={() => navigate(`/w/${workspaceId}/billing`)}
              className="mt-2 ps-6 text-xs font-medium underline"
            >
              Buy top-up credits
            </button>
          </div>
        )}

        {launchBlocked && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900" role="status">
            <p className="flex items-center gap-2 text-sm font-medium">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {t('campaigns.preview_step.blocked', 'Before you can launch:')}
            </p>
            <ul className="mt-2 space-y-1.5 ps-6 text-sm">
              {launchBlockers.map((blocker) => (
                <li key={`${blocker.step}:${blocker.message}`} className="flex flex-wrap items-baseline gap-x-2">
                  <span>{blocker.message}</span>
                  <button
                    type="button"
                    onClick={() => onEdit(blocker.step)}
                    className="text-xs font-medium underline hover:text-amber-950"
                  >
                    {t('campaigns.preview_step.go_to', 'Go to {{step}}', {
                      step: t(stepDef(blocker.step).labelKey, stepDef(blocker.step).label),
                    })}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <StepFooter
        onBack={onBack}
        primary={(
          <Button
            size="lg"
            onClick={() => setShowConfirm(true)}
            disabled={launching || launchBlocked || shortBy > 0}
            className="gap-2"
          >
            {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {scheduled ? 'Schedule Campaign' : 'Launch campaign'}
          </Button>
        )}
      />

      {/* Launch Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {scheduled ? 'Schedule Campaign?' : 'Launch Campaign Now?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will {scheduled ? 'schedule' : 'immediately send'} {isWhatsApp ? 'WhatsApp' : 'SMS'} messages to{' '}
              <strong>{recipientCount.toLocaleString()}</strong> recipients and deduct{' '}
              <strong>{formatCredits(estimatedCredits)}</strong> credits from your workspace. Messages that fail to
              send are refunded; this action cannot otherwise be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowConfirm(false);
                handleLaunch();
              }}
            >
              {scheduled ? 'Confirm Schedule' : 'Confirm & Send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function SummarySection({ step, onEdit, children }: { step: StepId; onEdit: (step: StepId) => void; children: ReactNode }) {
  const { t } = useTranslation();
  const def = stepDef(step);
  const label = t(def.labelKey, def.label);
  return (
    <section className="space-y-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={() => onEdit(step)}
          aria-label={`${t('campaigns.preview_step.edit', 'Edit')} — ${label}`}
        >
          <Pencil className="h-3 w-3" /> {t('campaigns.preview_step.edit', 'Edit')}
        </Button>
      </div>
      {children}
    </section>
  );
}

function SummaryRow({ label, value, multiline = false }: { label: string; value: ReactNode; multiline?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={multiline ? 'w-full whitespace-pre-line text-foreground' : 'min-w-0 text-end font-medium break-words'}
        dir="auto"
      >
        {value}
      </span>
    </div>
  );
}

function Missing({ text = 'Not set' }: { text?: string }) {
  return <span className="font-medium text-amber-600">{text}</span>;
}
